<template>
  <form
    id="communication-form"
    name="communcationForm"
    class="container mt-3"
    @submit.prevent="sendCommunication">
    <!-- Events -->
    <div class="row">
      <div class="col">
        <div class="row mt-3">
          <div class="col">
            <b-form-group label="Type">
              <b-form-radio
                v-model="message.type"
                :value="'email'">
                Email
              </b-form-radio>
              <b-form-radio
                v-model="message.type"
                :value="'text'">
                Text
              </b-form-radio>
            </b-form-group>
          </div>
        </div>

        <div>
          <label for="events">
            Events:
          </label>
          <!-- Select All -->
          <b-button
            type="button"
            variant="link"
            size="sm"
            @click="selectAllEvents">
            Select All
          </b-button>
          <!-- / Select All -->

          <!-- Deselect All -->
          <b-button
            type="button"
            variant="link"
            size="sm"
            @click="deselectAllEvents">
            Deselect All
          </b-button>
          <!-- / Deselect All -->
        </div>
        <b-form-select
          id="events"
          ref="events"
          name="events"
          v-model="selectedEvents"
          :options="events"
          :readonly="submitting"
          @input="deselectAllRegistrants(); selectAllRegistrants();"
          multiple>
        </b-form-select>
      </div>
    </div>
    <!-- / Events -->

    <!-- Registrants -->
    <div class="row mt-3">
      <div class="col">
        <div>
          <label for="registrants">
            Registrants:
          </label>
          <!-- Select All -->
          <b-button
            type="button"
            variant="link"
            size="sm"
            @click="selectAllRegistrants"
            :disabled="message.type === 'text'">
            Select All
          </b-button>
          <!-- / Select All -->

          <!-- Deselect All -->
          <b-button
            type="button"
            variant="link"
            size="sm"
            @click="deselectAllRegistrants"
            :disabled="message.type === 'text'">
            Deselect All
          </b-button>
          <!-- / Deselect All -->
        </div>
        <b-form-select
          id="registrants"
          ref="registrants"
          name="registrants"
          v-model="selectedRegistrants"
          :options="filteredRegistrants"
          :readonly="message.type === 'text'"
          multiple>
        </b-form-select>
      </div>
    </div>
    <!-- / Registrants -->

    <!-- Subject -->
    <div class="row mt-3">
      <div class="col">
        <label for="subject">Subject:</label>
        <b-form-input
          id="subject"
          ref="subject"
          name="subject"
          type="text"
          placeholder="Enter a subject"
          v-model="message.subject"
          :readonly="submitting || message.type === 'text'">
        </b-form-input>
      </div>
    </div>
    <!-- / Subject -->

    <!-- Body -->
    <div class="row mt-3">
      <div class="col">
        <label for="body">Body:
          <small v-if="message.type === 'text'" :class="{'text-danger': message.message && message.message.length > 300}">
            {{ message.message ? message.message.length : 0 }}/300 characters
          </small>
        </label>
        <b-form-textarea
          id="body"
          ref="body"
          name="body"
          rows="5"
          v-model="message.message"
          :readonly="submitting"
          placeholder="Enter a message body"
          no-resize>
        </b-form-textarea>
      </div>
    </div>
    <!-- / Body -->

    <!-- Attachments -->
    <div class="row mt-3">
      <div class="col">
        <label for="attachments">
          Attached Files:
          <small class="text-danger">
            Total file size must be less than 30MB.
          </small>
        </label>

        <!-- Attachments Table -->
        <b-table
          striped
          hover
          selectable
          small
          show-empty
          select-mode="multiple"
          selected-variant=""
          sort-by="name"
          class="file-attachments"
          @row-clicked="toggleAttachment(message.attachment_uuids, $event)"
          :items="fileOptions"
          :fields="attachmentFields"
          empty-text="No attached files.">
          <template
            slot="remove"
            slot-scope="{ item }">
            <b-button
              type="button"
              size="sm"
              variant="link"
              class="py-0 px-1">
              <div v-if="isFileAttached(message.attachment_uuids, item)">
                <font-awesome-icon icon="times"></font-awesome-icon>
                Remove
              </div>
              <div v-else>
                <font-awesome-icon icon="undo"></font-awesome-icon>
                Undo
              </div>
            </b-button>
          </template>
          <template
            slot="created"
            slot-scope="{ item }">
            {{ item.created | datetime }}
          </template>
        </b-table>
        <!-- / Attachments Table -->
      </div>
    </div>
    <!-- / Attachments -->

    <!-- Attach New File -->
    <div class="row mt-3">
      <div v-if="!message.id" class="col">
        <label for="attach-new-file">
          Upload File:
          <small class="d-block d-sm-inline text-danger">
            You must click "Upload" after selecting your file in order to attach it to your message.
          </small>
        </label>
        <b-input-group>
          <b-form-file
            id="attach-new-file"
            ref="attachNewFile"
            name="attachNewFile"
            v-model="attachment"
            placeholder="Choose a file..."
            drop-placeholder="Drop file here..."
            :disabled="submitting || message.type === 'text'">
          </b-form-file>
          <b-input-group-append>
            <b-button
              @click="uploadFile(message.attachment_uuids, 'organization_id', loggedInUser.actingOrg.id)"
              :disabled="!attachment || uploading || message.type === 'text'"
              variant="primary">
              Upload
            </b-button>
          </b-input-group-append>
        </b-input-group>
      </div>
    </div>
    <!-- / Attach New File -->

    <div class="corner-buttons">
      <!-- Save Event Button -->
      <b-button
        class="shadow"
        type="submit"
        variant="success"
        :disabled="isDisabled">
        <font-awesome-icon icon="paper-plane"></font-awesome-icon> Send
      </b-button>
      <!-- / Save Event Button -->
    </div>
  </form>
</template>

<script>
import _ from 'lodash';
import DataSvc from '../services/data';
import AppMixin from '../services/app-mixin';
import BrowserStorageMixin from '../services/browser-storage-mixin';
import DirtyForm from '../services/dirty-form';
import EventBus from '../services/event-bus';
import CommunicationMixin from '../services/communication-mixin';
import FileUploadMixin from '../services/file-upload-mixin';

export default {
  name: 'new-event-communication',
  mixins: [
    AppMixin,
    BrowserStorageMixin,
    DirtyForm,
    CommunicationMixin,
    FileUploadMixin,
  ],
  data() {
    return {
      events: [],
      selectedEvents: [],
      registrations: [],
      selectedRegistrants: [],
    };
  },
  beforeRouteLeave(to, from, next) {
    this.sessionStorageDelete('communication');
    next();
  },
  computed: {
    isDisabled() {
      return this.submitting;
    },
    messageDirty() {
      const messageJSON = JSON.stringify(this.message);
      const messageCopyJSON = JSON.stringify(this.messageCopy);
      return !this.message.id && messageJSON !== messageCopyJSON;
    },
    anyDirty() {
      return this.messageDirty;
    },
    filteredRegistrants() {
      const registrants = _(this.registrations)
        .filter(r => this.selectedEvents.includes(r.event_id)
          && r.registered
          && !r.waitlist
          && !r.cancelled)
        .map(r => ({
          value: r.registrant.id,
          text: `${r.registrant.full_name} - ${r.registrant.email}`,
        }))
        .uniqBy('value')
        .orderBy('text')
        .value();

      return registrants;
    },
    toRegistrantIDs() {
      const IDs = _(this.filteredRegistrants)
        .filter(r => this.selectedRegistrants.includes(r.value))
        .map('value')
        .value();
      return IDs;
    },
  },
  watch: {
    toRegistrantIDs(IDs) {
      this.message.registrant_ids = IDs;
    },
    'message.type': function(type) {
      if(type === 'text') {
        this.selectAllRegistrants();
      }
    }
  },
  methods: {
    isInvalid() {
      let invalid = false;
      if (!this.message.registrant_ids || this.message.registrant_ids.length === 0) {
        invalid = true;
        this.showWarning(
          'You must select registrants in order to send a message.',
          'No registrants selected',
        );
      }
      return invalid;
    },
    sendCommunication() {
      if (this.isInvalid()) return;

      let message = _.cloneDeep(this.message);
      if(message.type === 'text') {
        delete message.registrant_ids;
        if(message.attachment_uuids.length === 0) {
          delete message.attachment_uuids;
        }
        message.event_ids = this.selectedEvents;
      }

      //return;
      this.showToast('Sending message...');
      this.submitting = true;

      // if message already exists (send attempted) do a resend
      if (this.message.id) {
        message = {
          email_log_entry_id: this.message.id,
        };
      }

      message.organization_id = this.loggedInUser.actingOrg.id;

      // eslint-disable-next-line
      return DataSvc.sendCommunication(message)
        .then(() => this.delay(1000))
        .then(() => {
          this.showToast('Message sent!');
          this.messageCopy = _.cloneDeep(this.message);
          this.$router.push({
            name: 'communications',
          });
        })
        .finally(() => this.delay(1000))
        .finally(() => {
          this.submitting = false;
          this.hideToast();
        });
    },
    getEventsAndRegistrations() {
      const eventsPromise = DataSvc.getEvents(
        this.loggedInUser.actingOrg.id,
        this.loggedInUser.actingSeason.id,
      ).then((events) => {
        this.events = _(events)
          .map(e => ({
            value: e.id,
            text: e.name,
          }))
          .orderBy('text')
          .value();
      });

      const registrationsPromise = DataSvc.getRegistrations({
        organization_id: this.loggedInUser.actingOrg.id,
        season_id: this.loggedInUser.actingSeason.id,
      }).then((registrations) => {
        this.registrations = registrations;
      });

      return Promise.all([eventsPromise, registrationsPromise]);
    },
    selectAllEvents() {
      this.selectedEvents = _.map(this.events, 'value');
      this.selectAllRegistrants();
    },
    deselectAllEvents() {
      this.selectedEvents = [];
      this.deselectAllRegistrants();
    },
    selectAllRegistrants() {
      this.selectedRegistrants = _.map(this.filteredRegistrants, 'value');
    },
    deselectAllRegistrants() {
      this.selectedRegistrants = [];
    },
  },
  created() {
    this.showGlobalLoader('Loading data...');

    const initCommunicationEventNew = () => {
      const config = this.sessionStorageGet('communication', {});

      // get events and registrations
      const eventsAndRegistrationsPromise = this.getEventsAndRegistrations();

      // update events when season changes
      EventBus.$on('acting-season-changed', this.getEventsAndRegistrations);

      // watch for org change and route to events page
      EventBus.$on('acting-org-changed', this.goToEvents);

      if (config.eventIDs) {
        eventsAndRegistrationsPromise
          .then(() => {
            this.selectedEvents = config.eventIDs;
            this.selectAllRegistrants();
          });
      }

      return eventsAndRegistrationsPromise;
    };

    this.initPromise.then(initCommunicationEventNew).finally(this.hideGlobalLoader);
  },
  destroyed() {
    EventBus.$off('acting-season-changed', this.getEventsAndRegistrations);
    EventBus.$off('acting-org-changed', this.goToEvents);
  },
};
</script>

<style lang="scss">
#communication-form {
  #registrants,
  #events {
    height: 200px;
  }
}
</style>
